import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'
import {
  CreateBusinessCategoryFailure,
  CreateBusinessCategoryRequest,
  CreateBusinessCategorySuccess,
  CreateBusinessSubcategoryFailure,
  CreateBusinessSubcategoryRequest,
  CreateBusinessSubcategorySuccess,
  DeleteBusinessCategoryFailure,
  DeleteBusinessCategoryRequest,
  DeleteBusinessCategorySuccess,
  DeleteBusinessSubcategoryFailure,
  DeleteBusinessSubcategoryRequest,
  DeleteBusinessSubcategorySuccess,
  FetchBusinessCategoriesFailure,
  FetchBusinessCategoriesRequest,
  FetchBusinessCategoriesSuccess,
  FetchBusinessCategoryFailure,
  FetchBusinessCategoryRequest,
  FetchBusinessCategorySuccess,
  FetchBusinessSubcategoryFailure,
  FetchBusinessSubcategoryRequest,
  FetchBusinessSubcategorySuccess,
  UpdateBusinessCategoryFailure,
  UpdateBusinessCategoryRequest,
  UpdateBusinessCategorySuccess,
  UpdateBusinessSubcategoryFailure,
  UpdateBusinessSubcategoryRequest,
  UpdateBusinessSubcategorySuccess,
} from './business-categories.actions'
import { BusinessCategoriesService } from './business-categories.service'
import * as BusinessCategoriesTypes from './business-categories.types'

@Injectable()
export class BusinessCategoriesEffects {
  constructor(
    private actions: Actions,
    private businessCategoriesService: BusinessCategoriesService,
    private router: Router,
  ) {}

  FetchBusinessCategories$ = createEffect(() =>
    this.actions.pipe(
      ofType(BusinessCategoriesTypes.FETCH_BUSINESS_CATEGORIES_REQUEST),
      switchMap((action: FetchBusinessCategoriesRequest) => {
        return this.businessCategoriesService
          .fetchBusinessCategories(action.payload)
          .pipe(
            map((result) => {
              return new FetchBusinessCategoriesSuccess(
                result.data.results,
                result.data.totalCount,
                result.data.filteredCount,
              )
            }),
            catchError((err, caught) => {
              return of(new FetchBusinessCategoriesFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  FetchBusinessCategory$ = createEffect(() =>
    this.actions.pipe(
      ofType(BusinessCategoriesTypes.FETCH_BUSINESS_CATEGORY_REQUEST),
      switchMap((action: FetchBusinessCategoryRequest) => {
        return this.businessCategoriesService
          .fetchBusinessCategory(action.id, action.params)
          .pipe(
            map((result) => {
              return new FetchBusinessCategorySuccess(result.data)
            }),
            catchError((err, caught) => {
              return of(new FetchBusinessCategoryFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  DeleteBusinessCategory$ = createEffect(() =>
    this.actions.pipe(
      ofType(BusinessCategoriesTypes.DELETE_BUSINESS_CATEGORY_REQUEST),
      switchMap((action: DeleteBusinessCategoryRequest) => {
        return this.businessCategoriesService
          .deleteBusinessCategory(action.id)
          .pipe(
            switchMap((result) => {
              return [new DeleteBusinessCategorySuccess()]
            }),
            catchError((err, caught) => {
              return of(new DeleteBusinessCategoryFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  CreateBusinessCategory$ = createEffect(() =>
    this.actions.pipe(
      ofType(BusinessCategoriesTypes.CREATE_BUSINESS_CATEGORY_REQUEST),
      switchMap((action: CreateBusinessCategoryRequest) => {
        return this.businessCategoriesService
          .createBusinessCategory(action.payload)
          .pipe(
            switchMap((result) => {
              console.log(result)

              if (action.redirect) {
                this.router.navigate(['/admin/license-categories'])
              } else {
                this.router.navigate([
                  '/admin/license-categories',
                  result.data.id,
                  'update',
                ])
              }

              return [new CreateBusinessCategorySuccess()]
            }),
            catchError((err, caught) => {
              return of(new CreateBusinessCategoryFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  CreateBusinessSubcategory$ = createEffect(() =>
    this.actions.pipe(
      ofType(BusinessCategoriesTypes.CREATE_BUSINESS_SUBCATEGORY_REQUEST),
      switchMap((action: CreateBusinessSubcategoryRequest) => {
        return this.businessCategoriesService
          .createBusinessSubcategory(action.id, action.payload)
          .pipe(
            switchMap((result) => {
              this.router.navigate([
                '/admin/license-categories',
                action.id,
                'update',
              ])
              return [new CreateBusinessSubcategorySuccess()]
            }),
            catchError((err, caught) => {
              return of(new CreateBusinessSubcategoryFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  UpdateBusinessCategory$ = createEffect(() =>
    this.actions.pipe(
      ofType(BusinessCategoriesTypes.UPDATE_BUSINESS_CATEGORY_REQUEST),
      switchMap((action: UpdateBusinessCategoryRequest) => {
        return this.businessCategoriesService
          .updateBusinessCategory(action.id, action.payload)
          .pipe(
            switchMap((result) => {
              if (action.payload.redirect) {
                this.router.navigate(['/admin/license-categories'])
              }
              return [new UpdateBusinessCategorySuccess()]
            }),
            catchError((err, caught) => {
              return of(new UpdateBusinessCategoryFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  DeleteBusinessSubcategory$ = createEffect(() =>
    this.actions.pipe(
      ofType(BusinessCategoriesTypes.DELETE_BUSINESS_SUBCATEGORY_REQUEST),
      switchMap((action: DeleteBusinessSubcategoryRequest) => {
        return this.businessCategoriesService
          .deleteBusinessSubcategory(action.cid, action.id)
          .pipe(
            switchMap((result) => {
              return [new DeleteBusinessSubcategorySuccess()]
            }),
            catchError((err, caught) => {
              return of(new DeleteBusinessSubcategoryFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  FetchBusinessSubcategory$ = createEffect(() =>
    this.actions.pipe(
      ofType(BusinessCategoriesTypes.FETCH_BUSINESS_SUBCATEGORY_REQUEST),
      switchMap((action: FetchBusinessSubcategoryRequest) => {
        return this.businessCategoriesService
          .fetchBusinessSubcategory(action.id)
          .pipe(
            map((result) => {
              return new FetchBusinessSubcategorySuccess(result.data)
            }),
            catchError((err, caught) => {
              return of(new FetchBusinessSubcategoryFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  UpdateBusinessSubcategory$ = createEffect(() =>
    this.actions.pipe(
      ofType(BusinessCategoriesTypes.UPDATE_BUSINESS_SUBCATEGORY_REQUEST),
      switchMap((action: UpdateBusinessSubcategoryRequest) => {
        return this.businessCategoriesService
          .updateBusinessSubcategory(action.id, action.payload)
          .pipe(
            switchMap((result) => {
              this.router.navigate([
                '/admin/license-categories/',
                action.cid,
                'update',
              ])
              return [new UpdateBusinessSubcategorySuccess()]
            }),
            catchError((err, caught) => {
              return of(new UpdateBusinessSubcategoryFailure({ error: err }))
            }),
          )
      }),
    ),
  )
}
